import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { fetchAccounts, getAccounts, isEthereum, isMetaMask } from './utils';
import { ethers } from 'ethers';
import { emptyState, IState, Reducer } from './reducer';
import { useMoralis } from 'react-moralis';
import { Network } from '../Networks/useNetworks';
import { redirectToChromeStore } from '../../utils/redirector';
import { Contracts, IContractConfig } from '../../contracts/types';
import useAsyncEffect from '../../utils/hooks/effects/async';
import { IParent } from '../../components/common/types';
// import { useEffectOnce } from 'usehooks-ts';
import { userProfileConfig } from '../../contracts/userProfile/contract';
import { igoConfig } from '../../contracts/igo/contract';
import { hashupStoreConfig, hashupStoreV2Config } from '../../contracts/hashupStore/contract';
import { CARTRIDGE_ABI } from '../../contracts/token/abi';
import { CARTRIDGE_BYTECODE } from '../../contracts/token/bytecode';
import { hashConfig } from '../../contracts/hash/contract';
import { config } from 'src/config';
import { useEffectOnce } from 'src/lib/Hooks';
import axios from 'axios';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { string } from 'yup';
import { OPEN_API_URL } from 'src/constants';

interface IContext extends IState {
	login: () => void;
	logout: () => void;
	jwt: string;
}

const emptyContext: IContext = {
	...emptyState,
	login: () => null,
	logout: () => null,
	jwt: '',
};

const Context = createContext(emptyContext);

export const BlockchainProvider = ({ children }: IParent) => {
	/** @TODO: Deprecate */
	const [isEthereumCheckPending_, setIsEthereumCheckPending_] = useState(false);
	const [state, dispatch] = useReducer(Reducer, emptyContext);

	const [jwt, setJWT] = useLocalStorage<string>('JWT', '');

	const moralis = useMoralis();
	const contractConfig: { [p in Contracts]: IContractConfig } = useMemo(
		() => ({
			hashupStore: hashupStoreConfig[state.chainId],
			hashupStoreV2: hashupStoreV2Config[state.chainId],
			igo: igoConfig[state.chainId],
			userProfile: userProfileConfig[state.chainId],
			hash: hashConfig[state.chainId],
		}),
		[state.chainId]
	);

	/**
	 * Logs into wallet provider, Moralis and the application.
	 */
	const login = useCallback(async () => {
		if (!state.isEthereum) {
			redirectToChromeStore();
			return;
		}

		const accounts = await (window as any).ethereum.request({ method: 'eth_requestAccounts' }).catch((err: any) => {
			if (err.code === 4001) {
				// EIP-1193 userRejectedRequest error
				// If this happens, the user rejected the connection request.
				console.log('Please connect to MetaMask.');
			} else {
				console.error(err);
			}
		});

		console.log(accounts);

		if (accounts[0] || state.account) {
			axios
				.post(`${OPEN_API_URL}/v1/auth-migration/nonce-migration`, { address: accounts[0] })
				.then(async message => {
					(window as any).ethereum
						.request({
							method: 'personal_sign',
							params: [message.data, accounts[0]],
						})
						.then((res: any) => {
							axios
								.post(`${OPEN_API_URL}/v1/auth-migration/login-migration`, {
									address: accounts[0],
									signature: res,
								})
								.then((res: any) => {
									setJWT(res.data as string);
									dispatch({
										account: accounts[0],
									});
								});
						});
				});
		}
	}, [state.isEthereum, state.isLoggedIn, moralis.isAuthenticated]);

	/**
	 * Logs out of the wallet provider, Moralis and the application.
	 */
	const logout = useCallback(async () => {
		dispatch({
			isLoggedIn: emptyContext.isLoggedIn,
			account: emptyContext.account,
		});

		setJWT('');

		await moralis.logout();
	}, []);

	useEffect(() => {
		if (jwt != '' && state.account != emptyContext.account) {
			dispatch({
				isLoggedIn: true,
			});
		}
	}, [jwt, state.account]);

	/** Ethereum setup */
	useEffectOnce(() => {
		dispatch({ isEthereum: isEthereum() });
	});

	useEffect(() => {}, [state.account]);

	/** Contracts setup */
	useEffect(() => {
		if (!state.signer || !state.isChainValid) {
			return;
		}

		dispatch({
			contracts: {
				hashupStore: new ethers.Contract(
					config.blockchain.contracts.storeV1,
					contractConfig.hashupStore.abi,
					state.signer
				),
				hashupStoreV2: new ethers.Contract(
					config.blockchain.contracts.storeV2,
					contractConfig.hashupStoreV2.abi,
					state.signer
				),
				igo: new ethers.Contract(contractConfig.igo.address, contractConfig.igo.abi, state.signer),
				userProfile: new ethers.Contract(
					contractConfig.userProfile.address,
					contractConfig.userProfile.abi,
					state.signer
				),
				hash: new ethers.Contract(contractConfig.hash.address, contractConfig.hash.abi, state.signer),
			},
			contractFactories: {
				cartridge: new ethers.ContractFactory(CARTRIDGE_ABI, CARTRIDGE_BYTECODE, state.signer),
			},
		});
	}, [state.signer]);

	/** MetaMask presence check */
	useEffect(() => {
		if (state.isEthereum) {
			dispatch({ isMetaMask: isMetaMask() });
		}
	}, [state.isEthereum]);

	/** Account check and retrieval */
	useAsyncEffect(async () => {
		if (state.isEthereum) {
			const accounts = await getAccounts();
			if (!accounts.length) {
				dispatch({
					isLoggedIn: emptyContext.isLoggedIn,
					account: emptyContext.account,
					isLoading: false,
					checkedLogin: true,
				});
			} else {
				dispatch({
					account: accounts[0],
					isLoggedIn: true,
					isLoading: false,
					checkedLogin: false,
				});
			}
		} else if (isEthereumCheckPending_) {
			dispatch({
				isLoggedIn: emptyContext.isLoggedIn,
				isLoading: false,
			});
			setIsEthereumCheckPending_(false);
		}

		if (!isEthereumCheckPending_) {
			setTimeout(() => {
				setIsEthereumCheckPending_(state.isLoading);
			}, 150);
		}
	}, [state.isEthereum, isEthereumCheckPending_]);

	/** Account listener */
	useEffect(() => {
		if (!state.isEthereum) {
			return;
		}

		const onAccountsChanged = (accounts: any) => {
			if (!accounts || state.isLoggedIn) {
				return;
			}

			if (accounts.length) {
				if (accounts[0] != state.account) void logout();

				dispatch({
					account: accounts[0],
				});
			} else {
				void logout();
				window.location.assign('/');
			}
		};
		window.ethereum.on('accountsChanged', onAccountsChanged);
	}, [state.isEthereum, jwt]);

	/** Provider and signer retrieval */
	useEffect(() => {
		if (state.account === emptyContext.account) {
			dispatch({
				provider: emptyContext.provider,
				signer: emptyContext.signer,
			});

			return;
		}

		const provider = new ethers.providers.Web3Provider(window.ethereum);
		const signer = provider.getSigner();

		dispatch({
			provider: provider,
			signer: signer,
		});
	}, [state.account, state.chainId]);

	/** Network retrieval */
	useAsyncEffect(async () => {
		if (!state.isEthereum || !window.ethereum.request) {
			return;
		}

		const chainIdRaw = await window.ethereum.request({ method: 'eth_chainId' });

		dispatch({ chainId: parseInt(chainIdRaw, 16) });
	}, [state.isEthereum]);

	/** Network listener */
	useEffect(() => {
		if (!state.isEthereum) {
			return;
		}

		const onChainChanged = async (chainId: string) => {
			dispatch({ chainId: parseInt(chainId, 16) });
		};
		window.ethereum.on('chainChanged', onChainChanged);
	}, [state.isEthereum]);

	/** Network check */
	useAsyncEffect(async () => {
		const isCurrentNetworkSupported = state.chainId == config.blockchain.chainId;

		if (state.chainId == 0) return;

		dispatch({ isChainValid: isCurrentNetworkSupported });
		if (!window.ethereum.request) {
			return;
		}
		if (!isCurrentNetworkSupported)
			await window.ethereum.request({
				method: 'wallet_addEthereumChain',
				params:
					process.env.REACT_APP_ENVIRONMENT === 'dev'
						? [
								{
									chainId: '0x13881',
									chainName: 'Mumbai Testnet',
									nativeCurrency: {
										name: 'MATIC',
										symbol: 'MATIC',
										decimals: 18,
									},
									rpcUrls: ['https://matic-mumbai.chainstacklabs.com/'],
									blockExplorerUrls: ['https://mumbai.polygonscan.com/'],
								},
						  ]
						: [
								{
									chainId: '0x89',
									chainName: 'Polygon Mainnet',
									nativeCurrency: {
										name: 'MATIC',
										symbol: 'MATIC',
										decimals: 18,
									},
									rpcUrls: [
										'https://rpc-mainnet.matic.quiknode.pro/',
										'https://polygon-rpc.com/',
										'https://matic-mainnet-archive-rpc.bwarelabs.com/',
									],
									blockExplorerUrls: ['https://polygonscan.com/'],
								},
						  ],
			});
	}, [state.chainId]);

	return (
		<Context.Provider
			value={{
				...state,
				login,
				logout,
				jwt,
			}}
		>
			{children}
		</Context.Provider>
	);
};

export const useBlockchainProvider = () => useContext(Context);
